/**
 * What a subagent may change, judged on the change itself: the level before a tool ran and the draft after it.
 * A tool offered to a role can still reach past its row (Decor's `update_item` on a sofa, Catalog's `delete` on a
 * wall); the draft is then dropped whole and the model is told it was not its job.
 */
import type { Item, Level, ModelRef } from '../core/hp/types';
import type { CatalogNode } from '../state/catalog';
import tree from '../state/catalogTree.json';
import { isCatalogPart } from './catalogFilter';
import type { AgentRole } from './roles';

export type ItemKind = 'light' | 'kitchen' | 'decor' | 'furniture';
type Kinds = 'all' | 'none' | readonly ItemKind[];
export interface Rights {
  /** Walls, rooms, openings and the floor's own fields. */
  structure: boolean;
  /** Surfaces of walls, floors, ceilings, openings and items. */
  finish: boolean;
  /** Moving, turning and resizing items already placed. */
  arrange: Kinds;
  /** Adding, removing and replacing items. */
  stock: Kinds;
}

/** An untagged item no larger than this, in metres on its longest side, is small decor. */
export const DECOR_MAX_SIZE = 0.4;

const BRANCHES: ReadonlyArray<[ItemKind, readonly string[]]> = [
  ['light', ['Lighting', 'Build/Electricity', 'Electronic Devices/LightingOutletsSwitches']],
  ['kitchen', ['kitchens', 'Electronic Devices/KitchenAppliances', 'Storage/Base Cabinets', 'Storage/Wall Cabinets', 'Storage/High Cabinets',
    'Storage/Countertops', 'Storage/Kitchen Island', 'Storage/Kitchen Cart']],
  ['decor', ['Decoration', 'Seating furniture/Pillow', 'AllBed/Pillow', 'AllBed/Bedding']],
];
type Query = { anyTags: readonly string[]; allTags: readonly string[] };
let queries: Array<[ItemKind, Query[]]> | null = null;
/** Each branch as the tag queries of its lists and their chips, each by its own tags (a chip's alone, so a lamp tagged only for a kitchen island still counts). */
function branchQueries(): Array<[ItemKind, Query[]]> {
  if (queries) return queries;
  const find = (nodes: readonly CatalogNode[], id: string): CatalogNode | undefined => {
    for (const node of nodes) { if (node.id === id) return node; const deeper = node.children && find(node.children, id); if (deeper) return deeper; }
    return undefined;
  };
  const collect = (node: CatalogNode, out: Query[]) => {
    if (node.anyTags?.length || node.allTags?.length) out.push({ anyTags: node.anyTags ?? [], allTags: node.allTags ?? [] });
    for (const filter of node.filters ?? []) if (filter.anyTags.length || filter.allTags.length) out.push(filter);
    for (const child of node.children ?? []) collect(child, out);
    return out;
  };
  const nodes = tree as CatalogNode[];
  return queries = BRANCHES.map(([kind, ids]) => [kind, ids.flatMap(id => { const node = find(nodes, id); return node ? collect(node, []) : []; })]);
}
const matches = (query: Query, tags: ReadonlySet<string>) =>
  query.allTags.every(tag => tags.has(tag)) && (!query.anyTags.length || query.anyTags.some(tag => tags.has(tag)));

/** What kind an item is, from the facts the document carries; anything it cannot tell is furniture. */
export function itemKind(item: Pick<Item, 'light' | 'tags' | 'modules' | 'modelRef' | 'caption' | 'size'>): ItemKind {
  if (item.light) return 'light';
  if (item.tags?.length) {
    const tags = new Set(item.tags);
    for (const [kind, list] of branchQueries()) if (list.some(query => matches(query, tags))) return kind;
  }
  if (item.modules?.length) return 'kitchen';
  if (item.modelRef.kind === 'local') {
    const id = item.modelRef.id;
    if (/^lightings?\./.test(id)) return 'light';
    if (/(^|\.)kitchens?(\.|\/)/.test(id) || isCatalogPart({ id, title: item.caption ?? '' })) return 'kitchen';
    if (/^decor\./.test(id)) return 'decor';
  }
  return Math.max(item.size.w, item.size.h, item.size.d) <= DECOR_MAX_SIZE ? 'decor' : 'furniture';
}

/** A stable text of a value with the given keys left out; equal texts mean equal values. */
function signature(value: unknown, omit: readonly string[] = []): string {
  if (Array.isArray(value)) return '[' + value.map(entry => signature(entry)).join(',') + ']';
  if (value && typeof value === 'object') {
    return '{' + Object.keys(value).filter(key => !omit.includes(key) && (value as Record<string, unknown>)[key] !== undefined).sort()
      .map(key => JSON.stringify(key) + ':' + signature((value as Record<string, unknown>)[key])).join(',') + '}';
  }
  return JSON.stringify(value) ?? 'null';
}
const surfaceOf = (part: { surface?: unknown } | undefined) => part?.surface;
const WALL_FINISH = ['frontSurface', 'backSurface'];
const wallStructure = (wall: Level['walls'][number]) => signature({ ...wall, plinth: wall.plinth && { ...wall.plinth, surface: undefined },
  molding: wall.molding && { ...wall.molding, surface: undefined } }, WALL_FINISH);
const wallFinish = (wall: Level['walls'][number]) => signature([wall.frontSurface, wall.backSurface, surfaceOf(wall.plinth), surfaceOf(wall.molding)]);
type Finished = { materials?: unknown; modules?: readonly Finished[] };
/** A part with its materials left out at every depth of its modules: a kitchen's fronts are finish, not its shape. */
const bare = (part: Finished): Finished => ({ ...part, materials: undefined, modules: part.modules?.map(bare) });
const finishes = (part: Finished): unknown => [part.materials, part.modules?.map(finishes)];
const ROOM_FINISH = ['floorSurface', 'ceilingSurface'];

const allows = (kinds: Kinds, kind: ItemKind) => kinds === 'all' || (kinds !== 'none' && kinds.includes(kind));

/** Pairs of the same object before and after, by key; an object on one side only pairs with undefined. */
function pairs<T>(before: readonly T[], after: readonly T[], key: (value: T) => string | number): Array<[T | undefined, T | undefined]> {
  const old = new Map(before.map(value => [key(value), value] as const));
  const out: Array<[T | undefined, T | undefined]> = [];
  for (const value of after) { const was = old.get(key(value)); old.delete(key(value)); out.push([was, value]); }
  for (const value of old.values()) out.push([value, undefined]);
  return out;
}

const modelOf = (ref: ModelRef) => `${ref.kind}:${ref.id}`;

/** The first change outside the role's row, in words for the model; null when every change is the role's to make. */
export function rightsViolation(role: AgentRole, before: Level, after: Level): string | null {
  if (before === after) return null;
  const rights = role.rights;
  const refuse = (change: string) => `${role.name} (${role.id}) may not ${change}. Nothing was changed. Say in your report that this is not your job.`;
  const structure = (what: string) => rights.structure ? null : refuse(what);
  const finish = (what: string) => rights.finish ? null : refuse(what);

  /** A part of the plan: its own fields are structure, the listed surfaces are finish. */
  const part = <T>(was: T | undefined, now: T | undefined, what: string, shape: (value: T) => string, surfaces: (value: T) => string) =>
    !was || !now ? structure(`${now ? 'add' : 'remove'} ${what}`)
      : shape(was) !== shape(now) ? structure(`change ${what}`)
      : surfaces(was) !== surfaces(now) ? finish(`change the finish of ${what}`) : null;

  const PARTS = ['rooms', 'walls', 'openings', 'items'];
  if (signature(before, PARTS) !== signature(after, PARTS)) { const reason = structure('change the floor itself'); if (reason) return reason; }
  for (const [was, now] of pairs(before.walls, after.walls, wall => wall.id)) {
    const reason = part(was, now, 'a wall', wallStructure, wallFinish); if (reason) return reason;
  }
  for (const [was, now] of pairs(before.rooms, after.rooms, room => room.index)) {
    // Furnishing names the type of a room nobody typed (furnish_room, Catalog's alone).
    const typed = rights.stock === 'all' && was && now && !was.roomType ? { ...now, roomType: was.roomType } : now;
    const reason = part(was, typed, 'a room', room => signature(room, ROOM_FINISH), room => signature([room.floorSurface, room.ceilingSurface])); if (reason) return reason;
  }
  for (const [was, now] of pairs(before.openings, after.openings, opening => opening.id)) {
    const reason = part(was, now, 'a door or window', opening => signature(bare(opening)), opening => signature(finishes(opening))); if (reason) return reason;
  }
  for (const [was, now] of pairs(before.items, after.items, item => item.id)) {
    let reason: string | null = null;
    if (!was || !now) {
      const kind = itemKind((now ?? was)!);
      if (!allows(rights.stock, kind)) reason = refuse(`${now ? 'add' : 'remove'} ${kind === 'light' ? 'a light' : kind === 'kitchen' ? 'a kitchen part' : kind}`);
    } else if (modelOf(was.modelRef) !== modelOf(now.modelRef)) {
      const kinds = [itemKind(was), itemKind(now)];
      if (!kinds.every(kind => allows(rights.stock, kind))) reason = refuse('replace this item');
    } else {
      const reshaped = signature(bare(was)) !== signature(bare(now));
      if (reshaped) {
        const kinds = [itemKind(was), itemKind(now)];
        if (!kinds.every(kind => allows(rights.arrange, kind))) reason = refuse('move, turn or resize this item');
      }
      // A module taken out or put in takes its own finish with it; the item's own stays a finish.
      const look = (item: Item) => signature(reshaped ? item.materials : finishes(item));
      if (!reason && look(was) !== look(now)) reason = finish('change the materials of an item');
    }
    if (reason) return reason;
  }
  return null;
}
